<template>
    <div class="kpi_card">
        <div class="card_header is-flex is-align-items-center">
            <PieChartIcon class="header_icon" />
            <span class="card_title">Performance de clientes</span>
        </div>

        <div class="kpi_table_wrap">
        <table class="kpi_table">
            <thead>
                <tr>
                    <th class="metric_col">Métrica</th>
                    <th class="value_col has-text-right">Valor</th>
                    <th class="pct_col has-text-right">MoM</th>
                    <th class="pct_col has-text-right">YoY</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="kpi in kpis" :key="kpi.metric">
                    <th scope="row" class="metric_cell">
                        <svg v-if="metricIcon(kpi.metric)"
                            class="metric_icon" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" stroke-width="2"
                            stroke-linecap="round" stroke-linejoin="round"
                            aria-hidden="true">
                            <path :d="metricIcon(kpi.metric)" />
                        </svg>
                        <span class="metric_label">{{ kpi.metric }}</span>
                    </th>
                    <td class="value_cell has-text-right">{{ formatValue(kpi) }}</td>
                    <td class="pct_cell has-text-right" :class="pctClass(kpi.mom)">
                        {{ formatPct(kpi.mom) }}
                    </td>
                    <td class="pct_cell has-text-right" :class="pctClass(kpi.yoy)">
                        {{ formatPct(kpi.yoy) }}
                    </td>
                </tr>
            </tbody>
        </table>
        </div>
    </div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import { formatVariation } from "@/utils/percentage";
import PieChartIcon from "@/components/svg/PieChartIcon.vue";

// Formateador de números enteros (es-CL) module-scoped — `Intl.NumberFormat`
// es caro de instanciar y no depende de estado del componente.
const COUNT_FORMATTER = new Intl.NumberFormat("es-CL");

// Mapa metric → SVG path. Single-path por entrada (concatenamos subpaths con
// múltiples comandos M dentro del mismo d). Si la lista de métricas crece o
// pasa a ser data-driven desde el backend, conviene extraer estos a
// componentes propios en /components/svg/ — por ahora el set es chico y fijo.
const METRIC_ICON_PATHS = {
    // Tarjeta con línea divisoria (credit-card simplificado).
    "Ticket Promedio": "M3 7h18v10H3zM3 11h18",
    // Casita / storefront.
    "Cantidad de clientes": "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM9 22V12h6v10",
    // Refresh-cw (dos flechas circulares).
    "Clientes recurrentes": "M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15",
};

export default {
    name: "ClientsKpiCard",
    components: { PieChartIcon },
    props: {
        kpis: {
            type: Array,
            required: true,
            validator: (arr) => arr.every((k) => typeof k.metric === "string"),
        },
    },
    methods: {
        formatValue(kpi) {
            if (kpi.format === "currency") return parseToMoney(kpi.value);
            // "count" (default) — entero con separador de miles es-CL.
            return COUNT_FORMATTER.format(kpi.value);
        },
        formatPct(pct) {
            // ↑/↓ + valor, sin + en positivos (color verde carga la dirección).
            // Negativos sí muestran "-" — color solo no es suficiente para
            // accesibilidad.
            return formatVariation(pct, { withArrow: true, arrowStyle: "line" });
        },
        pctClass(pct) {
            if (pct == null) return "pct_cell--neutral";
            if (pct > 0) return "pct_cell--positive";
            if (pct < 0) return "pct_cell--negative";
            return "pct_cell--neutral";
        },
        metricIcon(metric) {
            return METRIC_ICON_PATHS[metric] ?? null;
        },
    },
};
</script>

<style scoped lang="scss">
.kpi_card {
    background: var(--report-bg);
    border-radius: 16px;
    border: 1px solid var(--report-border);
    box-shadow: 0 1px 2px rgba(10, 13, 20, 0.03);
    padding: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.card_header {
    margin-bottom: 12px;
    color: var(--report-text);
    flex-shrink: 0;

    .header_icon {
        margin-right: 8px;
        flex-shrink: 0;
    }

    .card_title {
        font-size: 14px;
        font-weight: 600;
    }
}

// Defensive: en Stage 1 son 3 filas; el wrap permite que la card respete su
// altura aunque Stage 2 sume más métricas.
.kpi_table_wrap {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

.kpi_table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;

    th,
    td {
        padding: 10px 8px;
        font-size: 14px;
        line-height: 20px;
    }

    thead th {
        color: var(--report-text-muted);
        font-size: 12px;
        font-weight: 600;
        border-bottom: 1px solid var(--report-divider-soft);
    }

    // Sólo font-weight acá — el color de cada celda lo define su clase
    // específica (.metric_label, .value_cell, .pct_cell--*). Si seteamos un
    // color genérico en `tbody td`, su especificidad (.kpi_table tbody td)
    // supera a `.pct_cell--positive` y las flechas pierden el verde/rojo
    // semántico.
    tbody th,
    tbody td {
        font-weight: 500;
    }

    tbody tr + tr th,
    tbody tr + tr td {
        border-top: 1px solid var(--report-divider-soft);
    }

    .metric_col { width: 50%; }
    .value_col  { width: 22%; }
    .pct_col    { width: 14%; }
}

.metric_cell {
    text-align: left;
    display: flex;
    align-items: center;
    gap: 10px;

    .metric_icon {
        width: 16px;
        height: 16px;
        flex-shrink: 0;
        color: var(--report-text-muted);
    }

    .metric_label {
        color: var(--report-text);
    }
}

.value_cell {
    color: var(--report-text);
    font-weight: 600;
}

.pct_cell--positive { color: var(--report-positive); }
.pct_cell--negative { color: var(--report-negative); }
.pct_cell--neutral  { color: var(--report-text-muted); }
</style>
